import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Stack,
  StackProps,
  ButtonProps,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import {
  createNewSubmissionByQuizId,
  getSubmissionState,
} from '../../../../store/SubmissionSlice';
import { getTopicState } from '../../../../store/TopicSlice';
import { findNextSubtopic } from '../../../V2/StudentDashboard/components/TabSection';
import { getSubTopicState } from '../../../../store/SubTopicSlice';
import { getUrlConvertedString } from '../../../../helper';

const stackProps: StackProps = {
  padding: '16px',
  textAlign: 'center',
  gap: '10px',
  sx: {
    minHeight: '189px',
    borderRadius: '20px',
    color: '#F8FAFC',
    alignItems: 'center',
    justifyContent: 'center',
    background: `url('${process.env.PUBLIC_URL}/images/quiz/star-watermark.png') top left/70px no-repeat, url('${process.env.PUBLIC_URL}/images/quiz/cup-star-watermark.png') bottom right/100px no-repeat, ${TP_COLORS.gray.darken}`,
  },
};

const buttonProps: ButtonProps = {
  variant: 'contained',
  sx: {
    minHeight: '47px',
    marginTop: '20px',
    backgroundColor: '#fff',
    color: '#000',
    fontWeight: 600,
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: '#f4f4f4',
    },
  },
};

export type TState = {
  title: string;
  subTitle: string;
  buttonText: string;
};

const initialState: TState = {
  title: '',
  subTitle: '',
  buttonText: '',
};

const CompletionCard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState<TState>(initialState);

  // Store
  const { submission } = useAppSelector(getSubmissionState);
  const { topicsWithSubTopics } = useAppSelector(getTopicState);
  const { subTopic } = useAppSelector(getSubTopicState);

  useEffect(() => {
    if (submission) {
      const studentScore = (submission.score / submission.totalScore) * 100;
      if (studentScore >= 90) {
        setState({
          title: 'Excellent Work! 👏',
          subTitle: 'Check marking and move to the next topic',
          buttonText: 'CONTINUE TO NEXT TOPIC',
        });
      } else if (studentScore > 60) {
        setState({
          title: 'Nice Effort! 💪',
          subTitle: 'Review the marking scheme and retry',
          buttonText: 'RETRY THE QUIZ',
        });
      } else {
        setState({
          title: 'Keep Going! 😊',
          subTitle: 'Review the marking scheme and revise the topic',
          buttonText: 'TAKE ME TO REVISION',
        });
      }
    }
  }, [submission]);

  const handleButtonClick = () => {
    if (submission && subTopic) {
      const studentScore = (submission.score / submission.totalScore) * 100;

      const urlConvertedSubject = getUrlConvertedString(subTopic.topic.subject);
      const urlConvertedCurriculum = getUrlConvertedString(
        subTopic.topic.curriculum
      );

      if (studentScore >= 90) {
        const nextSubtopic = findNextSubtopic(
          topicsWithSubTopics,
          subTopic._id
        );

        if (nextSubtopic) {
          const topicName =
            topicsWithSubTopics.find((t) => t._id === nextSubtopic.topic)
              ?.name ?? '';

          const urlConvertedSubTopic = getUrlConvertedString(nextSubtopic.name);
          const urlConvertedTopic = getUrlConvertedString(topicName);

          const url = `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${nextSubtopic._id}`;

          navigate(url);
        }
      } else if (studentScore > 60) {
        dispatch(
          createNewSubmissionByQuizId({
            subTopicId: subTopic._id,
            quizId: submission.quiz._id,
          })
        );
      } else {
        const urlConvertedSubTopic = getUrlConvertedString(subTopic.name);
        const urlConvertedTopic = getUrlConvertedString(subTopic.topic.name);

        const url = `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopic._id}`;

        navigate(url);
      }
    }
  };

  return (
    <Stack {...stackProps}>
      <Typography fontSize="22px" fontWeight="600">
        {state.title}
      </Typography>
      <Typography fontSize="14px" fontWeight="600" maxWidth="250px">
        {state.subTitle}
      </Typography>
      <Button {...buttonProps} onClick={handleButtonClick}>
        {state.buttonText}
      </Button>
    </Stack>
  );
};

export default CompletionCard;
